/** The Structure step's site loads: the wind speed, ground snow and exposure the check uses, each with where it
 * comes from (the AHJ's published design values, the ASCE Hazard Tool, or nothing), and the installer's override
 * (which wins and is printed as entered). US values in mph and psf; elsewhere the installer's values only. */
import type { S } from "../api/client";
import { Field, NumberInput, Select } from "../components/ui";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { loadSource, suggestedValue, type LoadValue } from "../lib/permitAhj";
import { fromDisplay, lengthUnit, snowUnit, toDisplay, windUnit } from "../lib/permitWorkspace";
import type { Ws } from "./PermitWorkspace";

interface Loads {
  wind?: LoadValue;
  snow?: LoadValue;
  exposure?: string;
  exposure_source?: "installer" | "suggested";
  exposure_reason?: string;
  edition?: string;
}

export function HazardLoads({ ws, set }: { ws: Ws; set: (patch: Partial<S["PermitStructuralInputs"]>) => void }) {
  const t = useT();
  const intl = useIntlShape();
  const units = ws.units;
  const imperial = units === "imperial";
  const off = !ws.canEdit;
  const st = ws.details.structural ?? {};
  const structural = ws.preview.data?.electrical.structural as (Record<string, unknown> & { loads?: Loads | null; zoning?: { height_m: number; height_source: string } | null }) | null | undefined;
  const loads = structural?.loads ?? null;
  const zoning = structural?.zoning ?? null;
  const windSuggested = suggestedValue(loads?.wind);
  const snowSuggested = suggestedValue(loads?.snow);
  const exposureHint = loads?.exposure_source === "suggested" ? loads.exposure : null;
  return (
    <div className="pw-form-grid" data-anchor="loads">
      <Field label={t("permit.hazard.wind.label")} hint={loads ? loadSource(t, loads.wind) : imperial ? t("permit.hazard.wind.hintDefault") : undefined}>
        <NumberInput
          value={st.wind_speed ?? null}
          disabled={off}
          onChange={(v) => set({ wind_speed: v, wind_speed_unit: windUnit(units) })}
          suffix={imperial ? "mph" : "m/s"}
          min={10}
          max={350}
          placeholder={windSuggested != null ? t("permit.hazard.suggestedFrom", { value: intl.formatNumber(windSuggested, { maximumFractionDigits: 0 }), source: loads?.wind?.kind === "ahj" ? "ahj" : "tool" }) : ""}
        />
      </Field>
      <Field label={t("permit.hazard.snow.label")} hint={loads ? loadSource(t, loads.snow) : undefined}>
        <NumberInput
          value={st.ground_snow ?? null}
          disabled={off}
          onChange={(v) => set({ ground_snow: v, ground_snow_unit: snowUnit(units) })}
          suffix={imperial ? "psf" : "kPa"}
          min={0}
          max={1000}
          placeholder={snowSuggested != null ? t("permit.hazard.suggestedFrom", { value: intl.formatNumber(snowSuggested, { maximumFractionDigits: 0 }), source: loads?.snow?.kind === "ahj" ? "ahj" : "tool" }) : ""}
        />
      </Field>
      <Field label={t("permit.hazard.exposure.label")} hint={exposureHint ? t("permit.hazard.exposure.suggestedHint", { reason: loads?.exposure_reason ?? exposureHint }) : st.exposure ? t("permit.loads.yourValue") : undefined}>
        <Select
          value={st.exposure ?? ""}
          disabled={off}
          onChange={(v) => set({ exposure: (v || null) as S["PermitStructuralInputs"]["exposure"] })}
          options={[
            { value: "", label: t("permit.hazard.exposure.suggested", { exposure: exposureHint ?? "C" }) },
            { value: "B", label: t("permit.hazard.exposure.b") },
            { value: "C", label: t("permit.hazard.exposure.c") },
            { value: "D", label: t("permit.hazard.exposure.d") },
          ]}
          ariaLabel={t("permit.hazard.exposure.label")}
        />
      </Field>
      {imperial && (
        <Field
          label={t("permit.hazard.roofHeight.label")}
          hint={zoning ? (zoning.height_source === "installer" ? t("permit.loads.yourValue") : zoning.height_source === "roof model" ? t("permit.hazard.roofHeight.fromModel") : t("permit.hazard.roofHeight.typical", { ft: 20 })) : undefined}
        >
          <NumberInput
            value={toDisplay(st.mean_roof_height_m, units, "long")}
            disabled={off}
            onChange={(v) => set({ mean_roof_height_m: fromDisplay(v, units, "long") })}
            suffix={lengthUnit(units, "long")}
            placeholder={zoning && toDisplay(zoning.height_m, units, "long") != null ? intl.formatNumber(toDisplay(zoning.height_m, units, "long")!) : ""}
          />
        </Field>
      )}
    </div>
  );
}
